import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit2, Plus, Save, ArrowUp, ArrowDown, Trash2, Eye, X } from 'lucide-react';
import { FIELD_TYPES, SECTIONS, FIELD_CONDITION_OPERATORS } from './constants';

/**
 * One row per field. On a desktop the row is a twelve-column grid; below lg
 * it is a toolbar — order, position, delete — over the same controls stacked
 * one per line under a label, because five unlabelled boxes squeezed into
 * 375px say nothing about which one is the key and which the section.
 */

// a label the phone shows over each control; the desktop grid has no room for
// it and the placeholders do that job there
const PhoneLabel = ({ children }) => (
  <span className="mb-0.5 block text-[10px] font-medium text-slate-500 lg:hidden">{children}</span>
);

const CONTROL = 'h-11 lg:h-9';
const ROW_BUTTON = 'flex h-11 w-11 items-center justify-center rounded-md lg:h-auto lg:w-auto lg:rounded-none';

export default function FieldsEditor({
  fields, editMode, onStartEdit, onCancel, onSave, onAdd, onRemove, onMove, onUpdate, saving, title, description,
  allFieldNames,
}) {
  const availableFields = allFieldNames || fields.map(f => f.name).filter(Boolean);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-slate-600 font-medium">{fields.length} {title?.toLowerCase()}</p>
          {description && <p className="text-xs text-slate-400">{description}</p>}
        </div>
        {!editMode ? (
          <Button size="sm" variant="outline" onClick={onStartEdit} className="h-11 lg:h-8" data-testid="edit-fields-btn">
            <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit Fields
          </Button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={onAdd} className="h-11 lg:h-8" data-testid="add-field-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add Field
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel} className="h-11 lg:h-8">Cancel</Button>
            <Button size="sm" onClick={onSave} disabled={saving} className="h-11 bg-green-600 hover:bg-green-700 text-white lg:h-8" data-testid="save-fields-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-y-auto border rounded-lg lg:h-[400px]">
        <div className="space-y-2 p-2">
          {fields.map((field, idx) => (
            <div key={field._key || field.id || idx} className="p-3 rounded-lg border bg-white" data-testid={`field-row-${idx}`}>
              {editMode ? (
                <div className="space-y-3">
                  <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                    <div className="flex items-center gap-2 lg:contents">
                      <div className="flex gap-0.5 lg:flex-col">
                        <button type="button" onClick={() => onMove(idx, -1)} disabled={idx === 0} aria-label="Move up"
                          className={`${ROW_BUTTON} border text-slate-400 hover:text-slate-600 disabled:opacity-30 lg:border-0`}>
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => onMove(idx, 1)} disabled={idx === fields.length - 1} aria-label="Move down"
                          className={`${ROW_BUTTON} border text-slate-400 hover:text-slate-600 disabled:opacity-30 lg:border-0`}>
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                      <button type="button" onClick={() => onRemove(idx)} aria-label="Remove field"
                        className={`${ROW_BUTTON} ml-auto border border-red-200 text-red-400 hover:text-red-600 lg:order-last lg:ml-0 lg:border-0`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex-1 flex flex-col gap-2 lg:grid lg:grid-cols-12 lg:items-center">
                      <div className="col-span-3">
                        <PhoneLabel>Label</PhoneLabel>
                        <Input
                          value={field.label}
                          onChange={(e) => onUpdate(idx, 'label', e.target.value)}
                          placeholder="Label (Display Name)"
                          className={`${CONTROL} text-sm`}
                        />
                      </div>
                      <div className="col-span-2">
                        <PhoneLabel>Field key</PhoneLabel>
                        <Input
                          value={field.name}
                          onChange={(e) => onUpdate(idx, 'name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                          placeholder="Field key"
                          className={`${CONTROL} text-xs font-mono`}
                        />
                      </div>
                      <div className="col-span-2">
                        <PhoneLabel>Type</PhoneLabel>
                        <Select value={field.type} onValueChange={(v) => onUpdate(idx, 'type', v)}>
                          <SelectTrigger className={`${CONTROL} text-xs`} aria-label="Field type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <PhoneLabel>Section</PhoneLabel>
                        <Select value={field.section} onValueChange={(v) => onUpdate(idx, 'section', v)}>
                          <SelectTrigger className={`${CONTROL} text-xs`} aria-label="Section"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        <label className="flex min-h-11 flex-1 items-center gap-2 rounded-md border px-3 text-xs lg:min-h-0 lg:flex-none lg:gap-1 lg:border-0 lg:px-0">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => onUpdate(idx, 'required', e.target.checked)}
                            className="h-5 w-5 rounded lg:h-auto lg:w-auto"
                          />
                          Req
                        </label>
                        <label className="flex min-h-11 flex-1 items-center gap-2 rounded-md border px-3 text-xs lg:min-h-0 lg:flex-none lg:gap-1 lg:border-0 lg:px-0" title="Auto-fill from order creation">
                          <input
                            type="checkbox"
                            checked={field.auto_filled || false}
                            onChange={(e) => onUpdate(idx, 'auto_filled', e.target.checked)}
                            className="h-5 w-5 rounded lg:h-auto lg:w-auto"
                          />
                          Auto
                        </label>
                      </div>
                    </div>
                  </div>

                  {(field.type === 'select' || field.type === 'checkbox_group') && (
                    <div className="lg:ml-10">
                      <Label className="text-[10px] text-slate-500">Options (comma-separated)</Label>
                      <Input
                        value={Array.isArray(field.options) ? field.options.join(', ') : (field.options || '')}
                        onChange={(e) => onUpdate(idx, 'options', e.target.value)}
                        placeholder="Option 1, Option 2, Option 3"
                        className={`${CONTROL} text-xs`}
                      />
                    </div>
                  )}

                  <div className="lg:ml-10">
                    {field.conditional ? (
                      <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] font-medium text-amber-700 flex items-center gap-1">
                            <Eye className="h-3 w-3" /> Visible when:
                          </p>
                          <button
                            type="button"
                            onClick={() => onUpdate(idx, 'conditional', null)}
                            className="flex h-11 w-11 items-center justify-center text-red-400 hover:text-red-600 lg:h-auto lg:w-auto"
                            title="Remove condition"
                            aria-label="Remove condition"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
                          <Select
                            value={field.conditional.field}
                            onValueChange={(v) => onUpdate(idx, 'conditional', { ...field.conditional, field: v })}
                          >
                            <SelectTrigger className={`${CONTROL} w-full text-xs lg:w-[140px]`} aria-label="Depends on field">
                              <SelectValue placeholder="Select field" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableFields.filter(f => f !== field.name).map(f => (
                                <SelectItem key={f} value={f}>{f}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={field.conditional.show_when || 'in'}
                            onValueChange={(v) => onUpdate(idx, 'conditional', { ...field.conditional, show_when: v })}
                          >
                            <SelectTrigger className={`${CONTROL} w-full text-xs lg:w-[120px]`} aria-label="Condition">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_CONDITION_OPERATORS.map(op => (
                                <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {field.conditional.show_when !== 'any' && (
                            <Input
                              value={Array.isArray(field.conditional.values) ? field.conditional.values.join(', ') : (field.conditional.values || '')}
                              onChange={(e) => onUpdate(idx, 'conditional', {
                                ...field.conditional,
                                values: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                              })}
                              placeholder="Value1, Value2"
                              className={`${CONTROL} flex-1 text-xs`}
                            />
                          )}
                        </div>
                      </div>
                    ) : (field.type === 'select' || field.type === 'toggle') ? (
                      <button
                        type="button"
                        onClick={() => onUpdate(idx, 'conditional', { field: '', values: [], show_when: 'in' })}
                        className="flex min-h-11 items-center gap-1 text-[10px] text-amber-600 hover:text-amber-700 hover:underline lg:min-h-0"
                      >
                        <Eye className="h-3 w-3" /> Add visibility condition
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-xs text-slate-400 w-6">{idx + 1}</span>
                  <span className="text-sm font-medium text-slate-700">{field.label}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{field.name}</span>
                  <Badge variant="outline" className="text-[10px]">{field.type}</Badge>
                  <span className="text-[10px] text-slate-400">{field.section}</span>
                  {field.required && <Badge className="text-[10px] bg-red-50 text-red-600">Required</Badge>}
                  {field.auto_filled && <Badge className="text-[10px] bg-blue-50 text-blue-600">Auto-fill</Badge>}
                  {field.options?.length > 0 && (
                    <span className="text-[10px] text-slate-400 truncate max-w-[200px]">[{field.options.join(', ')}]</span>
                  )}
                  {field.conditional && field.conditional.field && (
                    <Badge className="text-[10px] bg-amber-50 text-amber-700">
                      <Eye className="h-2.5 w-2.5 mr-0.5" />
                      {field.conditional.show_when === 'any' ? `if ${field.conditional.field}` :
                       `if ${field.conditional.field} ${field.conditional.show_when === 'in' ? '=' : '≠'} ${(field.conditional.values || []).join(', ')}`}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
